import { useCallback } from 'react'

import { useParams } from 'next/navigation'

import { Api } from '@zag-js/menu'
import { PropTypes } from '@zag-js/react'

import { useLanguageSwitcherContext } from '~/utils/providers'

import { ChevronIcon } from '~/components/Icons/ChevronIcon'
import { gt } from '~/lib/gt'
import { loadLocales } from '~/lib/gt/loader'

import styles from './LanguageSwitcher.module.css'
import { CurrentLocation, FlagProps, FlagShape, localeToCountry, UseLanguageSwitcherOutput } from './LanguageSwitcher.types'

const Flag = ({ language, shape = FlagShape.Rectangle }: FlagProps) => {
  return <span className={`fi fi-${localeToCountry[language || 'en'].toLowerCase()}${shape === FlagShape.Square ? ' fis' : ''}`} />
}

export function LanguageSwitcherButton({
  open,
  getTriggerProps,
  location,
  theme = styles,
}: Readonly<{
  open: boolean
  getTriggerProps: Api<PropTypes>['getTriggerProps']
  location: CurrentLocation
  theme?: typeof styles
}>) {
  const mergedTheme = { ...styles, ...theme }

  return (
    <button {...getTriggerProps()} className={mergedTheme.languageSwitcherButton}>
      <Flag language={location.key} />
      <p>{location.label}</p>
      <ChevronIcon style={{ transform: open ? 'rotate(90deg)' : '' }} height={10} width={10} />
    </button>
  )
}

export const LanguageSwitcherList = ({
  options,
  onClick,
  getContentProps,
  theme = styles,
}: {
  options: CurrentLocation[]
  onClick: (lang: string) => void
  getContentProps: Api<PropTypes>['getContentProps']
  theme?: typeof styles
}) => {
  const mergedTheme = { ...styles, ...theme }

  return (
    <ul {...getContentProps()} className={mergedTheme.languageSwitcherList}>
      {options.map(({ key, label, icon, current }) => (
        <li key={key} onClick={() => onClick(key)} className={mergedTheme.languageSwitcherListElement} aria-current={current}>
          {icon}
          {label}
        </li>
      ))}
    </ul>
  )
}

const prepareOption = (language: string) => ({
  key: language,
  label: gt.tp('Languages', language.toUpperCase()),
  icon: <Flag language={language} />,
})

export const useLanguageSwitcher = (): UseLanguageSwitcherOutput => {
  const locale = (useParams()?.lang || 'en') as string

  const { enabledLanguage } = useLanguageSwitcherContext()

  const onLanguageChange = useCallback((value: string) => {
    loadLocales(value).then(() => {
      window.location.reload()
    })
  }, [])

  const languages = Object.keys(enabledLanguage)

  const { options, currentLocation } = languages.reduce(
    (acc, cV) => {
      if (cV !== locale) acc.options.push(prepareOption(cV))
      else {
        const location = prepareOption(cV)
        acc.currentLocation = location
        acc.options.unshift({ ...location, current: true })
      }
      return acc
    },
    { options: [], currentLocation: {} } as any
  )

  return {
    options,
    currentLocation,
    onLanguageChange,
  }
}
